import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import Lottie from "react-lottie";

import axios from "../axios";
import * as locationAnimationData from "../assets/location.json";

const locationLottieOptions = {
  loop: true,
  autoplay: true,
  animationData: locationAnimationData,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
};

function LocationVerificationModal({ show, room_uuid, theme, onVerified }) {
  const { t } = useTranslation();
  const lang = sessionStorage.getItem("lang");

  const [locationModalError, setLocationModalError] = useState("");
  const [showLocationInstructions, setShowLocationInstructions] =
    useState(false);
  const [loadingAllowLocation, setLoadingAllowLocation] = useState(false);
  const [platformType, setPlatformType] = useState();
  const [showModal, setShowModal] = useState(false);

  const businessLocationRef = useRef(null);

  const getCurrentPosition = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation not supported"));
        return;
      }

      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 10000,
      });
    });

  const verifyPosition = (position) => {
    const userLat = position.coords.latitude;
    const userLng = position.coords.longitude;
    const business = businessLocationRef.current;

    if (!business) {
      setLocationModalError(t("location missing"));
      return;
    }

    const toRad = (value) => (value * Math.PI) / 180;
    const R = 6371;

    const bizLat = parseFloat(business.lat);
    const bizLng = parseFloat(business.lng);

    const dLat = toRad(bizLat - userLat);
    const dLon = toRad(bizLng - userLng);
    const lat1 = toRad(userLat);
    const lat2 = toRad(bizLat);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = R * c;

    if (distance > 0.5) {
      setShowModal(true);
      setLocationModalError(t("far location"));
    } else {
      setShowModal(false);
      onVerified();
    }
  };

  useEffect(() => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;

    if (/android/i.test(ua)) {
      setPlatformType("Android");
    } else if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
      setPlatformType("iOS");
    }
  }, []);

  useEffect(() => {
    if (!show || !room_uuid) return;

    setLocationModalError("");
    setShowLocationInstructions(false);
    setLoadingAllowLocation(false);
    setShowModal(false);

    axios
      .get(`/${room_uuid}/footer/qr-code`, {
        headers: {
          lang,
        },
      })
      .then(async (res) => {
        const loc = res.data?.data?.location;

        if (!loc?.lat || !loc?.lng) {
          onVerified();
          return;
        }

        businessLocationRef.current = loc;

        if (!navigator.permissions?.query) {
          setShowModal(true);
          return;
        }

        try {
          const permissionStatus = await navigator.permissions.query({
            name: "geolocation",
          });

          if (permissionStatus.state === "granted") {
            try {
              const position = await getCurrentPosition();

              verifyPosition(position);
            } catch (error) {
              console.error("Geolocation error:", error);

              setShowModal(true);
            }

            return;
          }

          setShowModal(true);
        } catch (error) {
          console.error("Permission API error:", error);

          setShowModal(true);
        }
      })
      .catch(() => {
        onVerified();
      });
  }, [show, room_uuid, lang]);

  const handleAllowLocation = async () => {
    setLoadingAllowLocation(true);
    setLocationModalError("");
    setShowLocationInstructions(false);

    if (!navigator.geolocation) {
      setLocationModalError(t("location not supported"));
      setLoadingAllowLocation(false);
      return;
    }

    try {
      const position = await getCurrentPosition();

      verifyPosition(position);
    } catch (error) {
      console.error("Geolocation error:", error);

      setShowLocationInstructions(true);
      setLocationModalError(t("location failed access"));
    } finally {
      setLoadingAllowLocation(false);
    }
  };

  return (
    <Modal
      className={`x ${theme ? "food-modal" : ""}`}
      show={show && showModal}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Body
        style={{
          padding: "2rem",
          height: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Lottie options={locationLottieOptions} height={200} width={200} />
        </div>

        <h2
          style={{
            marginTop: "1rem",
            fontSize: "18px",
            textAlign: "center",
            color: theme ? "white" : "black",
          }}
        >
          {t("allow location")}
        </h2>

        <p
          className="text-xl"
          style={{
            color: "gray",
            fontSize: "12px",
            textAlign: "center",
          }}
        >
          {t("location req")}
        </p>

        {locationModalError ? (
          <p
            className="text-danger mt-3"
            style={{
              textAlign: "center",
            }}
          >
            {locationModalError}

            {showLocationInstructions && (
              <>
                <br />
                <br />

                <Trans
                  i18nKey={
                    platformType === "iOS"
                      ? "allow location guideline iOS"
                      : "allow location guideline android"
                  }
                  components={{
                    bold: <strong />,
                  }}
                />
              </>
            )}
          </p>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <button
              type="button"
              onClick={handleAllowLocation}
              disabled={loadingAllowLocation}
              className="reg-btn"
              style={{
                width: "100%",
                textAlign: "center",
                marginBottom: "-1px",
                cursor: "pointer",
                opacity: loadingAllowLocation ? "70%" : "100%",
              }}
            >
              {t("request access")}
            </button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default LocationVerificationModal;
